import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CatalogFilters from "./components/CatalogFilters";
import CatalogGrid from "./components/CatalogGrid";
import { catalogProducts, sortOptions } from "@/mocks/catalogData";

export default function CatalogPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 6000]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("featured");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleMaterialToggle = (mat: string) => {
    setSelectedMaterials((prev) =>
      prev.includes(mat) ? prev.filter((m) => m !== mat) : [...prev, mat]
    );
  };

  const handleResetFilters = () => {
    setActiveCategory("all");
    setPriceRange([0, 6000]);
    setSelectedMaterials([]);
    setSearchQuery("");
  };

  const filtered = useMemo(() => {
    let result = [...catalogProducts];

    if (activeCategory !== "all") {
      result = result.filter((p) => p.category === activeCategory);
    }

    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    if (selectedMaterials.length > 0) {
      result = result.filter((p) =>
        selectedMaterials.some((m) =>
          p.material.toLowerCase().includes(m.toLowerCase())
        )
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.material.toLowerCase().includes(q)
      );
    }

    if (sortBy === "price-asc") result.sort((a, b) => a.price - b.price);
    else if (sortBy === "price-desc") result.sort((a, b) => b.price - a.price);
    else if (sortBy === "newest") result.sort((a, b) => b.id - a.id);

    return result;
  }, [activeCategory, priceRange, selectedMaterials, searchQuery, sortBy]);

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Navbar />

      {/* Page header */}
      <div className="w-full bg-[#1A1C1E] pt-28 pb-14 md:pt-32 md:pb-16 px-4 md:px-6 lg:px-8">
        <div className="mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6">
            <Link
              to="/"
              className="text-[11px] uppercase tracking-[0.14em] text-[#6C7278] hover:text-white transition-colors"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-[#6C7278] text-xs" />
            <span
              className="text-[11px] uppercase tracking-[0.14em] text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Full Catalog
            </span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p
                className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                / FORM Chicago
              </p>
              <h1
                className="text-4xl md:text-5xl lg:text-[56px] font-extrabold text-white tracking-[-0.02em] leading-tight"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                Full Catalog
              </h1>
            </div>
            <p
              className="text-sm text-[#6C7278] max-w-xs"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              Every piece available to experience in person at our West Loop showroom.
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="w-full bg-[#F7F5F2] border-b border-[#1A1C1E]/[0.08] sticky top-16 md:top-20 z-30">
        <div className="mx-auto px-4 md:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#6C7278] text-sm" />
            <input
              type="text"
              placeholder="Search pieces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-4 bg-white border border-[#1A1C1E]/[0.1] text-sm text-[#1A1C1E] placeholder:text-[#6C7278] focus:outline-none focus:border-[#B8422E] transition-colors"
              style={{ fontFamily: "'Public Sans', sans-serif", borderRadius: "4px" }}
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-9 pl-3 pr-8 bg-white border border-[#1A1C1E]/[0.1] text-[11px] uppercase tracking-[0.1em] text-[#1A1C1E] focus:outline-none focus:border-[#B8422E] transition-colors appearance-none cursor-pointer"
              style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-[#6C7278] text-sm pointer-events-none" />
          </div>

          {/* Mobile filter toggle */}
          <button
            className="lg:hidden h-9 px-4 flex items-center gap-2 border border-[#1A1C1E]/[0.1] bg-white text-[11px] uppercase tracking-[0.1em] text-[#1A1C1E] hover:border-[#B8422E] transition-colors whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <i className="ri-filter-3-line" />
            Filters
            {(activeCategory !== "all" || selectedMaterials.length > 0) && (
              <span className="w-4 h-4 flex items-center justify-center bg-[#B8422E] text-white text-[9px] rounded-full">
                {(activeCategory !== "all" ? 1 : 0) + selectedMaterials.length}
              </span>
            )}
          </button>

          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${viewMode === "grid" ? "bg-[#1A1C1E] text-white" : "text-[#6C7278] hover:text-[#1A1C1E]"}`}
              style={{ borderRadius: "4px" }}
              aria-label="Grid view"
            >
              <i className="ri-grid-line text-sm" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`w-9 h-9 flex items-center justify-center transition-colors cursor-pointer ${viewMode === "list" ? "bg-[#1A1C1E] text-white" : "text-[#6C7278] hover:text-[#1A1C1E]"}`}
              style={{ borderRadius: "4px" }}
              aria-label="List view"
            >
              <i className="ri-list-check text-sm" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile filters drawer */}
      {filtersOpen && (
        <div className="lg:hidden bg-white border-b border-[#1A1C1E]/[0.08] px-4 py-6">
          <CatalogFilters
            activeCategory={activeCategory}
            onCategoryChange={(v) => { setActiveCategory(v); setFiltersOpen(false); }}
            priceRange={priceRange}
            onPriceChange={setPriceRange}
            selectedMaterials={selectedMaterials}
            onMaterialToggle={handleMaterialToggle}
            totalCount={catalogProducts.length}
            filteredCount={filtered.length}
          />
        </div>
      )}

      {/* Main content */}
      <div className="mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-14 flex gap-10 lg:gap-16 items-start">
        {/* Sidebar — desktop only */}
        <div className="hidden lg:block sticky top-36 w-56 flex-shrink-0">
          <CatalogFilters
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            priceRange={priceRange}
            onPriceChange={setPriceRange}
            selectedMaterials={selectedMaterials}
            onMaterialToggle={handleMaterialToggle}
            totalCount={catalogProducts.length}
            filteredCount={filtered.length}
          />
        </div>

        {/* Product grid */}
        <div className="flex-1 min-w-0">
          {/* Active filters chips */}
          {(activeCategory !== "all" || selectedMaterials.length > 0 || searchQuery) && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {activeCategory !== "all" && (
                <span
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1C1E] text-white text-[11px] uppercase tracking-[0.1em]"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                >
                  {activeCategory}
                  <button onClick={() => setActiveCategory("all")} className="hover:text-[#B8422E] transition-colors cursor-pointer">
                    <i className="ri-close-line text-xs" />
                  </button>
                </span>
              )}
              {selectedMaterials.map((m) => (
                <span
                  key={m}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1C1E] text-white text-[11px] uppercase tracking-[0.1em]"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
                >
                  {m}
                  <button onClick={() => handleMaterialToggle(m)} className="hover:text-[#B8422E] transition-colors cursor-pointer">
                    <i className="ri-close-line text-xs" />
                  </button>
                </span>
              ))}
              <button
                onClick={handleResetFilters}
                className="text-[11px] uppercase tracking-[0.1em] text-[#6C7278] hover:text-[#B8422E] transition-colors cursor-pointer"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Clear all
              </button>
            </div>
          )}

          <CatalogGrid products={filtered} viewMode={viewMode} />

          {/* Bottom CTA */}
          <div className="mt-16 md:mt-20 py-12 px-8 bg-[#1A1C1E] text-center" style={{ borderRadius: "8px" }}>
            <p
              className="text-[11px] uppercase tracking-[0.22em] text-[#6C7278] mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              / Visit Us
            </p>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-white tracking-[-0.02em] mb-3"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              See Every Piece in Person
            </h2>
            <p
              className="text-sm text-[#6C7278] mb-8 max-w-md mx-auto"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              Our 8,000 sq ft West Loop showroom is open 7 days a week. Touch, sit, and experience every piece before you decide.
            </p>
            <a
              href="/#visit"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#B8422E] text-white text-[12px] font-semibold tracking-[0.1em] uppercase hover:bg-[#9E3826] transition-colors duration-300 whitespace-nowrap"
              style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
            >
              <i className="ri-map-pin-line" />
              Book a Design Consultation
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
